import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { Subject, filter, map } from 'rxjs';
import type { ListInterface } from '../models/list-interface';
import type { StudyDetailsInterface } from '../models/study-details.interface';
import { EStudyAction, EStudyStatus, Study } from '../models/study.interface';
import type { GetAlbumCasesQueryInterface } from './album-case.service';
import { GetAlbumCasesQuery } from './album-case.service';
import { environment } from '../../../environments/environment';
import type { SharedEntityInterface } from '../models/shared-study.interface';
import type { GetEntitySharesQueryInterface } from './study-share.service';
import { GetEntitySharesQuery, GetEntitySharesQueryVariables } from './study-share.service';
import { UserWithRoleInterface } from '../components/popups/share-study/components/share-by-email/share-by-email.component';
import { SexEnum, SterilisationEnum } from '../models/patient.interface';
import { SexAndSterilisation } from '../models/sexesAndSterilisations';
import { AppStateService } from './app-state.service';
import { SccStudyStatus } from '../../scc/shared/services/scc-study-data.interface';
import { TokenStorageService } from './token-storage.service';
import { AuthenticatedUrlUtil } from '../utils/authenticated-url.util';

export let StudiesQueryVariables: any = null;

export const StudiesQuery = gql`query getStudies ($order: [StudySortInput!], $where: StudyFilterInput, $clinicId: Long!, $take: Int!, $skip: Int!, $studySearchFormDto: StudySearchFormDtoInput!, $patientId: Long) {
  studies(order: $order, take: $take, skip: $skip, clinicId: $clinicId, where: $where, studySearchFormDto: $studySearchFormDto, patientId : $patientId) {
    items {
      id
      clinicId
      clinicName
      publicId
      createdAt
      examDate
      lastReportId
      orthancInstanceUid
      keywords {
        id
        label
        clinicId
        clinicName
      }
      shareUserIds
      shareRoles
      {
        roleId
        clinicSource
      }
      hasShares
      modalityTypeValues
      nbInstances
      nbReports
      nbAttachments
      ownerName
      patientId
      patientName
      patientSex
      patientSterilisation
      petName
      previewPublicId
      previewHash
      speciesName
      isNewOrUploading
      myPacsAction
      studyStatus
      name
      series {
        id
        serieStatus
        size
      }
      scc {
        id
      }
    }
    pageInfo {
      hasNextPage
    }
    totalCount
  }
}`;

export const StudiesSccQuery = gql`query getStudiesScc ($order: [StudySortInput!], $where: StudyFilterInput, $clinicId: Long!, $take: Int!, $skip: Int!, $studySearchFormDto: StudySearchFormDtoInput!, $patientId: Long) {
  studies(order: $order, take: $take, skip: $skip, clinicId: $clinicId, where: $where, studySearchFormDto: $studySearchFormDto, patientId : $patientId) {
    items {
      id
      clinicId
      clinicName
      clinicAddress
      clinicPhone
      publicId
      createdAt
      examDate
      lastReportId
      orthancInstanceUid
      hasShares
      modalityTypeValues
      nbInstances
      nbReports
      nbAttachments
      ownerName
      patientId
      patientName
      patientSex
      patientSterilisation
      petName
      previewPublicId
      previewHash
      speciesName
      modalityTypeValues
      isNewOrUploading
      myPacsAction
      studyStatus
      name
      clinic {
        id
        phone
        postalCode
        city
      }
      series {
        id
        previewPublicId
        previewHash
      }
      scc {
        id
        dogTranspondeurId
        canReviewerBeChanged
        canBeCanceled
        isEnded
        isEndedOk
        createdAt
        updatedAt
        isElbow
        isHip
        isAnfh
        isAs
        isPs
        isMvd
        isDcm
        isVa
        isLtv
        isOcd
        breed
        sedative
        reference
        price
        status
        datePayedByClinic
        datePayedByOwner
        dateReviewed
        dateReviewedDisagreement
        dateEnded
        dateCanceled
        dismissedReason
        dismissedMessage
        dismissedBy {
          id
          firstName
          lastName
          orderNum
        }
        reviewAllowed
        reviewTimeout
        birthDate
        name
        uploadReport {
          id
          path
        }
        report {
          id
          path
        }
        reviewer1 {
          id
          firstName
          lastName
          orderNum
        }
        reviewer2 {
          id
          firstName
          lastName
          orderNum
        }
        reviewer3 {
          id
          firstName
          lastName
          orderNum
        }
        uploader {
          id
          firstName
          lastName
          orderNum
          email
        }
        reviewer1FormData {
          id
          dateConfirmed
          isActive
          report {
            id
            path
          }
        }
        reviewer2FormData {
          id
          dateConfirmed
          isActive
          report {
            id
            path
          }
        }
        reviewer3FormData {
          id
          dateConfirmed
          isActive
          report {
            id
            path
          }
        }
        isInternalAppeal
        originalStudyId
        originalStudyReference
        originalStudyEntityId
        internalAppealDiseaseName
      }
    }
    pageInfo {
      hasNextPage
    }
    totalCount
  }
}`;

export const GetStudyDetailsQuery  = gql`query getStudyDetail ($studyId: Long!) {
  study(studyId: $studyId) {
    id
    isPublic
    name
    clinicId
    clinicName
    clinicAddress
    clinicPhone
    previewHash
    publicId
    createdAt
    orthancInstanceUid
    shareUserIds
    cases
    {
      id
    }
    shareRoles
    {
      roleId
      clinicSource
    }
    hasShares
    examDate
    keywords {
      id
      label
      clinicId
      clinicName
    }
    modalityTypeValues
    nbInstances
    nbReports
    nbAttachments
    ownerName
    patientId
    patientName
    patientSex
    patientBirthDate
    patientSterilisation
    patientPid
    petName
    previewPublicId
    previewHash
    speciesName
    speciesId
    modalityTypeValues
    isNewOrUploading
    myPacsAction
    studyStatus
    storageMode
    willBeDeletedAt
    attachments {
      id
      name
      type
      originalName
      path
    }
    reports {
      id
      htmlContent: htmlContentWithToken
      updatedAt
      name
      isLocked
    }
    series {
      id
      previewPublicId
      previewHash
      modalityType {
        description
        value
      }
      modalityTypeDescription
      nbInstances
      serieStatus
      size
    }
    scc {
      id
      dogTranspondeurId
      createdAt
      updatedAt
      isElbow
      isHip
      isAnfh
      isAs
      isPs
      isMvd
      isDcm
      isVa
      isLtv
      isOcd
      sedative
      reference
      breed
      price
      status
      datePayedByClinic
      datePayedByOwner
      dateReviewed
      dateReviewedDisagreement
      dateEnded
      dateCanceled
      dismissedReason
      dismissedMessage
      dismissedBy {
        id
        firstName
        lastName
        orderNum
      }
      dysplasiaElbow
      dysplasiaHip
      comment
      isReviewer
      isEnded
      isEndedOk
      reviewAllowed
      birthDate
      sex
      name
      transpondeurInDicomTags
      appealDiseases
      isInternalAppeal
      originalStudyId
      originalStudyReference
      originalStudyEntityId
      internalAppealDiseaseName
      uploadReport {
        id
        path
      }
      report {
        id
        path
      }
      reviewer1 {
        id
        firstName
        lastName
        orderNum
        email
      }
      reviewer2 {
        id
        firstName
        lastName
        orderNum
        email
      }
      reviewer3 {
        id
        firstName
        lastName
        orderNum
        email
      }
      uploader {
        id
        firstName
        lastName
        orderNum
        email
      }
      report {
        id
        path
      }
      reviewer1FormData {
        id
        dateConfirmed
        isActive
        report {
          id
          path
        }
      }
      reviewer2FormData {
        id
        dateConfirmed
        isActive
        report {
          id
          path
        }
      }
      reviewer3FormData {
        id
        dateConfirmed
        isActive
        report {
          id
          path
        }
      }
      myReportFormData {
        id
        createdAt
        updatedAt
        dateConfirmed
        identificationComplete
        identificationUnforgeable
        lateralisedRadio
        hipPelvicsSymmetry
        hipFemurParallel
        hipVisibleKneecaps
        hipMedianPositionedKneecaps
        elbowIncidenceExtensionRight
        elbowIncidenceFlexionRight
        elbowIncidenceRotationRight
        elbowIncidenceExtensionLeft
        elbowIncidenceFlexionLeft
        elbowIncidenceRotationLeft
        hipAcetabulumLeft
        hipAcetabulumRight
        hipCranialAcetabularRimLeft
        hipCranialAcetabularRimRight
        hipCranialAcetabularCoverLeft
        hipCranialAcetabularCoverRight
        hipDorsalAcetabularCoverLeft
        hipDorsalAcetabularCoverRight
        hipFemurParallel
        hipHeadAndNeckOsteophytoseCephalicLeft
        hipHeadAndNeckOsteophytoseCephalicRight
        hipHeadAndNeckOsteophytoseFemoralLeft
        hipHeadAndNeckOsteophytoseFemoralRight
        hipHeadAndNeckShapeVolumeLeft
        hipHeadAndNeckShapeVolumeRight
        hipHeadCenterPositionLeft
        hipHeadCenterPositionRight
        hipJointRelationshipsLeft
        hipJointRelationshipsRight
        hipMedianPositionedKneecaps
        hipNorbergOlssonAngleLeft
        hipNorbergOlssonAngleRight
        hipOsteophytoseAcetabularLeft
        hipOsteophytoseAcetabularRight
        hipOsteophytoseCaudalLeft
        hipOsteophytoseCaudalRight
        hipPelvicsSymmetry
        hipPoscDensificationLeft
        hipPoscDensificationRight
        hipResultLeft
        hipResultRight
        hipResult
        hipVisibleKneecaps
        elbowAnomalyNonUnionRight
        elbowAnomalyCoronoidalFragmentationRight
        elbowAnomalyOsteochondriteRight
        elbowAnomalyArticularIncongruenceRight
        elbowAnomalyCalcificationRight
        elbowOsteoarthritisManifestationsOsteophytesEdgesRight
        elbowOsteoarthritisManifestationsBoneDensificationRight
        elbowOsteoarthritisManifestationsOsteophytesMedialCompartmentRight
        elbowOsteoarthritisManifestationsOsteophytesLateralProfilesRight
        elbowAnomalyNonUnionLeft
        elbowAnomalyCoronoidalFragmentationLeft
        elbowAnomalyOsteochondriteLeft
        elbowAnomalyArticularIncongruenceLeft
        elbowAnomalyCalcificationLeft
        elbowOsteoarthritisManifestationsOsteophytesEdgesLeft
        elbowOsteoarthritisManifestationsBoneDensificationLeft
        elbowOsteoarthritisManifestationsOsteophytesMedialCompartmentLeft
        elbowOsteoarthritisManifestationsOsteophytesLateralProfilesLeft
        elbowResultLeft
        elbowResultRight
        elbowResult
        anfhResult
        asResult
        psResult
        mvdResult
        dcmResult
        vaResult
        ltvResult
        ocdResult
        comment
        report {
          id
          path
        }
        attachment {
          id
          name
          type
          originalName
        }
      }
    }
  }
}`;

@Injectable({
  providedIn: 'root'
})
export class StudiesService {
  private readonly _studyChanged: Subject<Study> = new Subject<Study>();
  public get studyChanged(): Subject<Study> {
    return this._studyChanged;
  }

  constructor(
    private readonly apollo: Apollo,
    private readonly translateService: TranslateService,
    private readonly appStateService: AppStateService,
    private readonly tokenStorageService: TokenStorageService
  ) {
    tokenStorageService.token.pipe(filter(t => !!t)).subscribe(_ => {
      this.listenStudiesChanged();
    });
  }

  listenStudiesChanged() {
    this.apollo.subscribe<{ onStudyChanged: Study }>({
      query: gql`subscription onStudyChanged {
        onStudyChanged {
          id
          clinicId
          clinicName
          clinicAddress
          clinicPhone
          publicId
          createdAt
          examDate
          lastReportId
          keywords {
            id
            label
            clinicId
            clinicName
          }
          modalityTypeValues
          patientSterilisation
          orthancInstanceUid
          nbInstances
          nbReports
          nbAttachments
          ownerName
          patientId
          patientName
          patientSex
          petName
          previewPublicId
          previewHash
          speciesName
          modalityTypeValues
          isNewOrUploading
          myPacsAction
          studyStatus
          name
          shareUserIds
          series {
            id
            previewHash
            previewPublicId
            serieStatus
            size
          }
          scc {
            id
          }
          shareRoles
          {
            roleId
            clinicSource
          }
          hasShares
          cases {
            id
            name
            type
            createdAt
            nbAttachments
            albumId
            study {
              id
              clinicId
              publicId
              createdAt
              clinicName
              keywords {
                id
                label
                clinicId
                clinicName
              }
              modalityTypeValues
              nbInstances
              nbReports
              nbAttachments
              ownerName
              patientName
              patientSex
              petName
              previewPublicId
              speciesName
              modalityTypeValues
              isNewOrUploading
              myPacsAction
              studyStatus
              previewHash
            }
          }
        }
      }`
    }).subscribe((result) => {
      if (result.data?.onStudyChanged) {
        const study = result.data.onStudyChanged;
        console.log('study changed :' + study.studyStatus, study);
        if (!study.myPacsAction || study.myPacsAction === EStudyAction.Dicomizer) {
          // Read the data from our cache for this query.
          const queryToUse = this.appStateService.isScc ? StudiesSccQuery : StudiesQuery;

          try {
            const studiesQuery = this.apollo.client.readQuery<GetStudiesReponse>({ query: queryToUse, variables: StudiesQueryVariables }) ;
            if (studiesQuery) {
              const listStudies = studiesQuery.studies;
              let listStudyItems = listStudies.items.map(s=> ({ ...s }));
              // Dédup par id : qu'il s'agisse d'une mise à jour ou d'un nouvel
              // ajout, une étude ne doit figurer qu'une seule fois dans le cache
              // Apollo. Combiné au prepend optimiste dédupliqué côté composant
              // (StudiesComponent.studyChangedEvent), cela garantit qu'une étude
              // entre dans la liste au plus une fois (bug #8).
              const studyFound = listStudyItems.find(s=>s.id === study.id);

              if (studyFound) {
                // Update existing study in place
                const index = listStudyItems.findIndex(s => s.id === study.id);
                listStudyItems[index] = study;
              } else {
                // Add new study at the beginning
                listStudyItems = [study, ...listStudyItems];
              }

              const newStudies = {
                studies : {
                  __typename: 'StudiesCollectionSegment',
                  items : listStudyItems,
                  pageInfo : listStudies.pageInfo,
                  totalCount: studyFound ? listStudies.totalCount : listStudies.totalCount + 1
                }
              };
              this.apollo.client.writeQuery({ query: queryToUse, variables : StudiesQueryVariables, data : newStudies  });
              console.log('Successfully updated study in cache');
            } else {
              console.log('Studies query not found in cache, unable to update');
            }
          } catch (error) {
            console.error('Error updating study cache:', error);
          }
        } else {

          // mise à jour albumcase
          if (study.cases?.length) {
            const firstCase = study.cases[0];
            const albumCasesQuery = this.apollo.client.readQuery<GetAlbumCasesQueryInterface>({ query: GetAlbumCasesQuery, variables: { albumId: study.cases[0].albumId } });
            if (albumCasesQuery) {
              const albumCases = { ...albumCasesQuery.albumCases };

              let cases = (albumCases.cases).map(albumCases=> ({ ...albumCases }));

              const caseFound = cases.find(c=> c.id === firstCase.id);
              if (caseFound) {
                cases = cases.filter(c=>c.id !== caseFound?.id);
              }

              cases = [study.cases[0], ...cases].sort((a, b) => a.name.localeCompare(b.name));
              albumCases.cases = cases;
              this.apollo.client.writeQuery<GetAlbumCasesQueryInterface>({ query: GetAlbumCasesQuery, data : { albumCases } });

            }
          }
        }

        this.studyChanged.next(study);
      }
    });
  }

  getStudies(
    skip: number,
    take: number,
    clinicId: number,
    search?: StudySearchFormInterface,
    patientId: number | undefined = undefined,
    sccSearch: StudySearchSccInterface | undefined = undefined,
    sort: string | undefined = undefined,
    sortDirection: 'asc' | 'desc' | '' | undefined = undefined
  ): Observable<ListInterface<Study>> {
    // Conversion des enums frontend → backend (respecter la casse exacte des enums C#)
    const sexes = search?.sexesAndSterilisations?.map(s => {
      const sexValue = SexEnum[s.sex]; // "Male" ou "Female"
      const sterilisationValue = SterilisationEnum[s.sterilisation]; // "Sterilized" ou "NotSterilized"

      return {
        sex: sexValue, // Garder la casse exacte (Male, Female)
        sterilisation: sterilisationValue === 'NotSterilized' ? 'Notsterilized' : sterilisationValue // Fix: NotSterilized → Notsterilized
      };
    });

    const searchVariables: any = {
      patientId,
      clinicId,
      skip,
      take,
      studySearchFormDto : {
        // Filtres de combinaisons sexe/stérilisation
        sexesAndSterilisations : sexes,

        // Filtres de présence
        hasReport: search?.hasReport,
        hasShares: search?.hasShares,

        // Filtre par taille
        studySize: search?.studySize,

        // Nouveaux filtres ajoutés au DTO
        species: search?.species,
        modalities: search?.modalities,
        keywords: search?.keywords,

        // Filtres de dates
        startDate: search?.startDate?.toISOString(),
        endDate: search?.endDate?.toISOString(),

        // Recherche textuelle
        searchText: search?.text
      },
      order: {
        createdAt: 'DESC'
      },
      // where est maintenant vide car tout passe par studySearchFormDto
      where: undefined
    };

    if (sort && sortDirection) {
      switch (sort) {
        case 'name':
          searchVariables.order = {
            patient: {
              petName: sortDirection.toUpperCase(),
              ownerName: sortDirection.toUpperCase(),
            }
          };
          break;
        case 'date':
          searchVariables.order = {
            examDate: sortDirection.toUpperCase(),
          };
          break;
        case 'species':
          searchVariables.order = {
            patient: {
              species: {
                name: sortDirection.toUpperCase(),
              }
            }
          };
          break;
        case 'sex':
          searchVariables.order = {
            patient: {
              sexe: sortDirection.toUpperCase(),
            }
          };
          break;
        default:
          break;
      }
    }

    if (this.appStateService.isScc) {
      searchVariables.studySearchFormDto.origin = 'SCC';
      searchVariables.studySearchFormDto.scc = sccSearch;
    }

    if (search?.sourceFilter) {
      searchVariables.studySearchFormDto.sourceFilter = search.sourceFilter;
    }

    // RECHERCHE SIMPLE (fulltext) - utilisée uniquement si pas de recherche avancée
    if (!search?.isAdvanced && (search?.fulltext?.length ?? 0) >= 1) {
      searchVariables.where = {
        or: [
          {
            name: {
              or: search?.fulltext?.map(value => ({
                startsWith: value
              }))
            },
          },
          {
            patient: {
              or: [
                {
                  pid: {
                    or: search?.fulltext?.map(value => ({
                      startsWith: value
                    }))
                  }
                },
                {
                  petName: {
                    or: search?.fulltext?.map(value => ({
                      startsWith: value
                    }))
                  }
                },
                {
                  ownerName: {
                    or: search?.fulltext?.map(value => ({
                      startsWith: value
                    }))
                  }
                },
                {
                  species: {
                    name: {
                      or: search?.fulltext?.map(value => ({
                        startsWith: value
                      }))
                    }
                  }
                }
              ]
            }
          },
          {
            series: {
              some: {
                modalityType: {
                  value: {
                    or: search?.fulltext?.map(value => ({
                      startsWith: value
                    }))
                  }
                }
              }
            }
          },
          {
            studyKeywords: {
              some: {
                keyword: {
                  label: {
                    or: search?.fulltext?.map(value => ({
                      startsWith: value
                    }))
                  }
                }
              }
            }
          }
        ]
      };
    }

    // console.log(searchVariables, JSON.stringify(searchVariables));
    StudiesQueryVariables = searchVariables;

    return this.apollo.watchQuery<{ studies: ListInterface<Study> }>({
      variables: searchVariables,
      query: this.appStateService.isScc ? StudiesSccQuery : StudiesQuery,
      fetchPolicy: 'network-only'
    }).valueChanges.pipe(map(res => ({ ...res.data.studies })));
  }

  getStudyDetails(studyId: number, withCache = true): Observable<StudyDetailsInterface> {
    return this.apollo.query<{ study: StudyDetailsInterface }>({
      query : GetStudyDetailsQuery,
      variables: { studyId },
      fetchPolicy: withCache ? undefined : 'no-cache',
    }).pipe(map(res => res.data.study));
  }

  getStudyDetailsWatched(studyId: number): Observable<StudyDetailsInterface> {
    return this.apollo.watchQuery<{ study: StudyDetailsInterface }>({
      query : GetStudyDetailsQuery,
      variables: { studyId }
    }).valueChanges.pipe(map(res => res.data.study));
  }

  deleteStudy(studyId: number): Observable<Study | undefined> {
    return this.apollo.mutate<{ deleteStudy: Study }>({
      mutation: gql`mutation deleteStudy($studyId: Long!) {
        deleteStudy(studyId: $studyId) {
          id
        }
      }`,
      variables: {
        studyId
      },
      refetchQueries: [{
        query : GetStudyDetailsQuery,
        variables: { studyId }
      }],
      update: (store, { data }) => {
        if (data?.deleteStudy) {
          const studiesQuery = store.readQuery<GetStudiesReponse>({ query: StudiesQuery, variables: StudiesQueryVariables }) ;
          if (studiesQuery) {
            const listStudies = studiesQuery.studies;
            let listStudyItems = listStudies.items.map(s=> ({ ...s }));
            const studyFound = listStudyItems.find(s=>s.id === studyId);
            let totalCount = listStudies.totalCount;
            if (studyFound) {
              listStudyItems = listStudyItems.filter(s=> s.id !== studyId);
              totalCount--;
            }

            const newStudies = {
              studies : {
                items : listStudyItems,
                pageInfo : listStudies.pageInfo,
                totalCount
              }
            };
            store.writeQuery({ query: StudiesQuery, variables : StudiesQueryVariables, data : newStudies  });
          }
        }
      },
    }).pipe(map(res =>res.data ? res.data.deleteStudy : undefined));
  }

  updateStudyVisibility(studyId: number, isPublic: boolean): Observable<StudyDetailsInterface | undefined> {
    return this.apollo.mutate<{ updateStudyVisibility: StudyDetailsInterface }>({
      mutation: gql`mutation updateStudyVisibility($studyId: Long!, $isPublic: Boolean!) {
        updateStudyVisibility(studyId: $studyId, isPublic: $isPublic) {
          id
          isPublic
        }
      }`,
      variables: {
        studyId,
        isPublic
      },
      refetchQueries: [{
        query : GetStudyDetailsQuery,
        variables: { studyId }
      }],
    }).pipe(map(res =>res.data ? res.data.updateStudyVisibility : undefined));
  }

  shareStudyToUsersByWeb(studyId: number, userIds: number[], clinicIds: number[], shareWithMyGroup: boolean): Observable<ShareStudyToUsersResultDto | undefined> {
    return this.apollo.mutate<ShareStudyToUsersByWebResponse>({
      mutation: gql`mutation shareStudyToUsersByWeb($studyId: Long!, $userIds: [Long!], $clinicIds: [Long!], $shareWithMyGroup: Boolean!) {
        shareStudyToUsersByWeb(studyId: $studyId, userIds: $userIds, clinicIds: $clinicIds, shareWithMyGroup: $shareWithMyGroup) {
          study
          {
            id
            name
            patientName
          }
          shares
          {
            id
            targetName,
            shareTargetType
            targetId
          }
        }
      }`,
      variables: {
        studyId,
        userIds,
        clinicIds,
        shareWithMyGroup
      },
      update: (store, { data }) => {

        if (data?.shareStudyToUsersByWeb) {

          // Read the data from our cache for this query.
          const getEntitySharesQuery = store.readQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, variables: GetEntitySharesQueryVariables });
          if (getEntitySharesQuery) {
            let shares = getEntitySharesQuery.sharedEntities.map(shares => ({ ...shares }));
            data?.shareStudyToUsersByWeb.shares.forEach(share => {
              if (!shares.map(k => k.id).includes(share.id)) {
                shares.push(share);
              }
            });

            shares = shares.sort((a, b) => a.targetName.localeCompare(b.targetName));

            store.writeQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, data: { sharedEntities :shares }, variables: GetEntitySharesQueryVariables });
          }
        }
      },
    }).pipe(map(res =>res.data ? res.data.shareStudyToUsersByWeb : undefined));
  }

  shareStudyToUsersByEmail(studyId: number, newUsers: UserWithRoleInterface[], userIds: number[], referingVetIds: number[] = []): Observable<ShareStudyToUsersResultDto | undefined> {
    return this.apollo.mutate<ShareStudyByEmailResponse>({
      mutation: gql`mutation shareStudyToUsersByEmail($dto: ShareStudyByEmailInputDtoInput!) {
        shareStudyByEmail(dto: $dto) {
          study
          {
            id
            name
            patientName
          }
          shares
          {
            id
            targetName,
            shareTargetType
            targetId
          }
        }
      }`,
      variables: {
        dto: {
          studyId,
          userIds,
          newUsers,
          referingVetIds,
          locale: this.translateService.currentLang,
        }
      },
      update: (store, { data }) => {

        if (data?.shareStudyByEmail) {

          // Read the data from our cache for this query.
          const getEntitySharesQuery = store.readQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, variables: GetEntitySharesQueryVariables });
          if (getEntitySharesQuery) {
            let shares = getEntitySharesQuery.sharedEntities.map(shares => ({ ...shares }));
            data?.shareStudyByEmail.shares.forEach(share => {
              if (!shares.map(k => k.id).includes(share.id)) {
                shares.push(share);
              }
            });

            shares = shares.sort((a, b) => a.targetName.localeCompare(b.targetName));

            store.writeQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, data: { sharedEntities :shares }, variables: GetEntitySharesQueryVariables });
          }
        }
      },
    }).pipe(map(res =>res.data ? res.data.shareStudyByEmail : undefined));
  }

  shareStudyToUsersBySoftware(studyId: number): Observable<boolean> {
    return this.apollo.mutate<{ generateAndExportZipForStudy: boolean }>({
      mutation: gql`mutation GenerateAndExportZipForStudy($studyId: Long!) {
        generateAndExportZipForStudy(studyId: $studyId)
      }`,
      variables: {
        studyId
      },
    }).pipe(map(res =>res.data?.generateAndExportZipForStudy ?? false));
  }

  getDownloadStudyZipUrl(publicId: string) {
    // Return full URL with apiUrl - token is added via Authorization header by HttpClient
    return `${environment.apiUrl}/pacs/getStudyZip/${publicId}`;
  }

  updateStudyStorageMode(studyId: number, storageMode: StudyStorageMode): Observable<StudyDetailsInterface | undefined> {
    return this.apollo.mutate<{ updateStudyStorageMode: StudyDetailsInterface }>({
      mutation: gql`mutation updateStudyStorageMode($studyId: Long!, $storageMode: StudyStorageMode!) {
        updateStudyStorageMode(studyId: $studyId, storageMode: $storageMode) {
          id
          storageMode
          willBeDeletedAt
        }
      }`,
      variables: {
        studyId,
        storageMode
      },
      refetchQueries: [{
        query : GetStudyDetailsQuery,
        variables: { studyId }
      }],
    }).pipe(map(res =>res.data ? res.data.updateStudyStorageMode : undefined));
  }

  public loadImage(study: Study): string {
    // Default image
    let currentPreview = '/assets/img/empty-states/empty_other.png';

    if (study.studyStatus === EStudyStatus.Uploading) {
      // Uploading image
      currentPreview = '/assets/img/upload.png';
    } else {
      if (study.previewPublicId) {
        currentPreview = `${environment.apiUrl}/instance/public-preview/${study.previewPublicId}.png`;
      }
    }

    return currentPreview;
  }

  private dateToIsoString(date: Date, isEndOfDay = false): string {
    date.setHours(isEndOfDay ? 23 : 0);
    date.setMinutes(isEndOfDay ? 59 : 0);
    date.setSeconds(isEndOfDay ? 59 : 0);
    date.setMilliseconds(0);
    return date.toISOString();
  }


}

export interface StudySearchSccInterface {
  // eslint-disable-next-line @typescript-eslint/ban-types
  status?: string | SccStudyStatus | null;
  reference?: string;
  isInternalAppeal?: boolean;
}

export interface StudySearchFormInterface {
  fulltext?: string[];
  text?: string;
  modalities?: string[];
  keywords?: string[];
  species?: string[];
  startDate?: Date;
  endDate?: Date;
  isAdvanced: boolean;
  sexes?: string[];
  sterilisations?: string[];
  sexesAndSterilisations?: SexAndSterilisation[];
  hasReport?: boolean;
  hasShares?: boolean;
  studySize?: string;
  sourceFilter?: string;
}

export interface GetStudiesReponse {
  studies: ListInterface<Study>;
}

export interface ShareStudyToUsersByWebResponse {
  shareStudyToUsersByWeb: ShareStudyToUsersResultDto;
}

export interface ShareStudyToUsersResultDto {
  study: Study;
  shares: SharedEntityInterface[];
}

export enum StudyStorageMode {
  OneMonth = 'ONE_MONTH',
  OneYear = 'ONE_YEAR',
  TwoYears = 'TWO_YEARS',
  Infinite = 'INFINITE'
}

export interface ShareStudyByEmailResponse {
  shareStudyByEmail: ShareStudyToUsersResultDto;
}
